/**
 * Cliente de la API (/api/*). La cookie de sesión es HttpOnly: el navegador nunca la ve.
 * Toda solicitud que modifica lleva X-Requested-With (defensa CSRF junto con SameSite=Strict).
 */
import type { Capability, Role, SheetName, Snapshot } from '@redmil/shared';

export interface CurrentUser {
  username: string;
  name: string;
  roles: Role[];
  roleLabels: string[];
  capabilities: Capability[];
  writeSheets: SheetName[];
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
  }
}

async function call<T>(method: string, path: string, body?: unknown, headers: Record<string, string> = {}): Promise<{ data: T; res: Response }> {
  const res = await fetch(path, {
    method,
    credentials: 'same-origin',
    headers: { accept: 'application/json', 'x-requested-with': 'redmil', ...(body ? { 'content-type': 'application/json' } : {}), ...headers },
    body: body ? JSON.stringify(body) : undefined,
  });
  if (res.status === 304) return { data: null as T, res };
  const text = await res.text();
  let data: unknown = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    throw new ApiError(res.status, 'El servidor respondió algo inesperado.');
  }
  if (!res.ok) throw new ApiError(res.status, (data as { error?: string })?.error ?? `Error ${res.status}`);
  return { data: data as T, res };
}

export async function getStatus(): Promise<{ authenticated: boolean; user?: CurrentUser; adminConfigured?: boolean }> {
  return (await call<{ authenticated: boolean; user?: CurrentUser; adminConfigured?: boolean }>('GET', '/api/auth/status')).data;
}

export async function login(username: string, password: string, remember: boolean): Promise<CurrentUser> {
  return (await call<{ user: CurrentUser }>('POST', '/api/auth/login', { username, password, remember })).data.user;
}

export async function logout(): Promise<void> {
  await call('POST', '/api/auth/logout');
}

let lastEtag: string | null = null;
let lastSnapshot: Snapshot | null = null;

/** Snapshot con ETag: si no cambió, el servidor responde 304 y se reutiliza el anterior. */
export async function getSnapshot(): Promise<{ snapshot: Snapshot; dataTime: number; serverTime: number; changed: boolean }> {
  const { data, res } = await call<Snapshot>('GET', '/api/snapshot', undefined, lastEtag && lastSnapshot ? { 'if-none-match': lastEtag } : {});
  const serverTime = Number(res.headers.get('x-server-time') ?? Date.now());
  const dataTime = Number(res.headers.get('x-data-time') ?? serverTime);
  if (res.status === 304 && lastSnapshot) return { snapshot: lastSnapshot, dataTime, serverTime, changed: false };
  lastEtag = res.headers.get('etag');
  lastSnapshot = data;
  return { snapshot: data, dataTime, serverTime, changed: true };
}
